<script lang="ts">
	import { mutation } from "@api/graphql.js";
	import sendWebMessage from "@api/mutations/sendWebMessage.graphql";
	import Button from "@components/common/Button.svelte";
	import Link from "@components/common/Link.svelte";
	import Modal from "@components/common/Modal.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import { LinkVariant } from "@components/common/getLinkClass.js";
	import Form from "@components/form/Form.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import LabelledTextarea from "@components/form/LabelledTextarea.svelte";
	import type { HTMLFormAttributes } from "svelte/elements";
	import type { WebMessageInput } from "@core/schema/WebMessage.js";
	import { WebMessageType } from "@core/schema/WebMessageType.js";
	import { getPrivacyPolicyPath } from "@core/schema/paths/getPrivacyPolicyPath.js";
	import type { Complete } from "@core/utils/Complete.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";

	const { ...rest }: Omit<HTMLFormAttributes, "onsubmit"> = $props();
	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	let showSuccessModal = $state<boolean>(false);
	let errorHappened = $state<boolean>(false);
	let gdprApprovedAt = $state<string | null>(null);
	function onGdprToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		gdprApprovedAt = input.checked ? new Date().toISOString() : null;
	}

	const send = createPromiseWithStatus(async (input: Omit<WebMessageInput, "careerDetail">) =>
		mutation<boolean, { input: typeof input }>(PUBLIC_GRAPHQL_ENDPOINT, sendWebMessage, { input }),
	);

	async function submit(event: SubmitEvent): Promise<void> {
		event.preventDefault();
		if (!gdprApprovedAt) {
			throw new Error("GDPR must be approved.");
		}

		errorHappened = false;

		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const name = formData.get("name") as string;
		const phoneNumber = formData.get("phoneNumber") as string;
		const email = formData.get("email") as string;
		const webUrl = (formData.get("webUrl") as string | null) ?? undefined;
		const companyName = (formData.get("companyName") as string | null) ?? undefined;
		const message = formData.get("message") as string;

		try {
			const input: Complete<Omit<WebMessageInput, "careerDetail">> = {
				name,
				phoneNumber,
				email,
				type: WebMessageType.Contact,
				gdprApprovedAt,
				contactDetail: {
					webUrl: webUrl ? webUrl : undefined,
					companyName: companyName ? companyName : undefined,
					message,
				},
				locale: tenant.locale,
			};
			await send.call(input);
			showSuccessModal = true;
			form.reset();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			errorHappened = true;
		}
	}
</script>

<Modal show={showSuccessModal}>
	<p class="text-xl text-fstonyx">Děkujeme za zprávu</p>
	<div class=" items-left flex flex-col gap-8">
		<p>
			Vaše zpráva byla úspěšně odeslána.
			<br />
			Brzy se vám ozveme.
		</p>

		<Button
			variant={ButtonVariant.Phlox}
			class="ml-auto"
			onclick={(): void => {
				showSuccessModal = false;
			}}
		>
			Rozumím
		</Button>
	</div>
</Modal>

<div class="flex flex-col items-center gap-8">
	<Form {...rest} onsubmit={submit} class={["grid grid-cols-1 lg:grid-cols-2 gap-4", rest.class]}>
		<LabelledInput name="name" required>Jméno a přijmení</LabelledInput>
		<LabelledInput name="companyName">Firma</LabelledInput>
		<LabelledInput type="email" name="email" required>E-mail</LabelledInput>
		<LabelledInput name="phoneNumber" type="tel" required>Telefonní číslo</LabelledInput>
		<LabelledInput type="url" name="webUrl" wrapperClass="col-span-full">
			Máte web, na který se můžeme předem podívat?
		</LabelledInput>

		<LabelledTextarea name="message" wrapperClass="col-span-full" required>Zpráva</LabelledTextarea>

		<LabelledInput type="checkbox" wrapperClass="col-span-full" onchange={onGdprToggle} required>
			Souhlasím se
			<Link variant={LinkVariant.Underline} href={getPrivacyPolicyPath(tenant)}>
				Zásadami zpracování osobních údajů
			</Link>
		</LabelledInput>

		<Button
			variant={ButtonVariant.Green}
			disabled={send.value.status === "loading"}
			class="place-self-center col-span-full"
		>
			{#if send.value.status === "loading"}
				Odesílání…
			{:else}
				Odeslat
			{/if}
		</Button>
	</Form>

	{#if errorHappened}
		<p class="text-fstphlox">
			Omlouváme se, ale při odesílání formuláře došlo k chybě. Zkuste to prosím znovu nebo později.
		</p>
	{/if}
</div>
